import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Car, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatUSD, formatDate } from "@/lib/format";

const IRS_RATE_2026 = 0.70;

const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  expense_date: todayStr(),
  category: "Mileage",
  amount: "",
  miles: "",
  vendor: "",
  notes: "",
};

const inputCls = "mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900";

export default function DealExpenseDialog({ deal, onClose }) {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!deal) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/deals/${deal.id}/expenses`),
        api.get("/expenses/categories"),
      ]);
      setExpenses(r1.data.expenses);
      setTotal(r1.data.total);
      setCategories(r2.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [deal]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        expense_date: form.expense_date,
        category: form.category,
        amount: parseFloat(form.amount) || 0,
        vendor: form.vendor || null,
        notes: form.notes || null,
        deal_id: deal.id,
      };
      if (form.category === "Mileage" && form.miles) {
        payload.miles = parseFloat(form.miles);
      }
      await api.post("/expenses", payload);
      toast.success("Expense added to deal");
      setForm({ ...emptyForm, category: form.category });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this expense from the deal?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense removed");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isMileage = form.category === "Mileage";
  const milesNum = parseFloat(form.miles) || 0;

  return (
    <Dialog open={!!deal} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-none max-w-3xl border-neutral-300" data-testid="deal-expense-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Deal Expenses</div>
          <DialogTitle className="font-serif text-2xl">{deal?.property_name}</DialogTitle>
          {deal?.client_name && <div className="text-xs font-mono text-neutral-500">Client: {deal.client_name}</div>}
        </DialogHeader>

        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-4 border-y border-neutral-200 py-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Sale Price</div>
            <div className="font-mono text-base">{formatUSD(deal?.sale_price || 0)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Net Commission</div>
            <div className="font-mono text-base" style={{ color: "var(--profit)" }}>{formatUSD(deal?.net_commission || 0)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Expenses on this Deal</div>
            <div className="font-mono text-base" style={{ color: "var(--expense)" }}>{formatUSD(total)}</div>
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={submit} className="border border-neutral-200 p-3 mb-4" data-testid="deal-expense-form">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Date</Label>
              <Input type="date" required value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className={inputCls} data-testid="deal-expense-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, miles: "" })}>
                <SelectTrigger className="mt-1 rounded-none border-neutral-300 focus:ring-0" data-testid="deal-expense-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isMileage ? (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
                  <Car className="h-3 w-3" strokeWidth={1.5} /> Miles
                </Label>
                <Input type="number" step="0.1" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} className={`${inputCls} font-mono`} data-testid="deal-expense-miles" />
              </div>
            ) : (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Amount</Label>
                <Input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`${inputCls} font-mono`} data-testid="deal-expense-amount" />
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Vendor</Label>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inputCls} data-testid="deal-expense-vendor" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="deal-expense-notes" />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-10" data-testid="deal-expense-submit">
                <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add
              </Button>
            </div>
          </div>
          {isMileage && milesNum > 0 && (
            <div className="mt-2 font-mono text-xs text-neutral-600 pt-2 border-t border-neutral-100">
              {milesNum} miles × ${IRS_RATE_2026.toFixed(2)}/mi (IRS 2026) ={" "}
              <span className="font-semibold text-neutral-900">{formatUSD(milesNum * IRS_RATE_2026)}</span>
            </div>
          )}
        </form>

        {/* List */}
        <div className="border border-neutral-200 max-h-72 overflow-y-auto" data-testid="deal-expense-list">
          <div className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-neutral-200 bg-neutral-50 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            <div className="col-span-2 flex items-center gap-1"><Receipt className="h-3 w-3" strokeWidth={1.5} /> Date</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-3">Vendor / Notes</div>
            <div className="col-span-2">Miles</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-1" />
          </div>
          {loading ? (
            <div className="px-3 py-6 text-sm text-neutral-500">Loading…</div>
          ) : expenses.length === 0 ? (
            <div className="px-3 py-6 text-sm text-neutral-500">No expenses logged for this deal yet.</div>
          ) : (
            expenses.map((e) => (
              <div key={e.id} className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-neutral-100 last:border-b-0 items-center text-sm" data-testid={`deal-expense-row-${e.id}`}>
                <div className="col-span-2 font-mono text-xs">{formatDate(e.expense_date)}</div>
                <div className="col-span-2">{e.category}</div>
                <div className="col-span-3 text-neutral-600 truncate">
                  {e.vendor && <span className="font-medium text-neutral-800">{e.vendor}</span>}
                  {e.vendor && e.notes && <span className="mx-1 text-neutral-400">·</span>}
                  {e.notes}
                </div>
                <div className="col-span-2 font-mono text-xs text-neutral-500">{e.miles ? `${e.miles} mi` : "—"}</div>
                <div className="col-span-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(e.amount)}</div>
                <div className="col-span-1 text-right">
                  <button onClick={() => remove(e.id)} className="text-neutral-400 hover:text-red-700" data-testid={`deal-expense-delete-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="text-xs text-neutral-500 pt-2 border-t border-neutral-200">
          Expenses added here automatically appear in the main Expenses ledger and roll up into your tax report.
        </div>
      </DialogContent>
    </Dialog>
  );
}
